import { useEffect, useRef, useState } from 'react'
import MediumModal from '../Modal/MediumModal'
import './addFurnizor.css'

const NEW_SUPPLIER_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/newSupplier'
const UPDATE_SUPPLIER_API_URL = 'https://dyt8xo9cba.execute-api.eu-central-1.amazonaws.com/techStage/updateSupplier'

const emptySupplier = {
    id: null,
    logo: '',
    alias: '',
    imageUpload: null,
    nume: '',
    cui: '',
    website: '',
    website_username: '',
    website_password: '',
    note: ''
}

const emptyContact = {
    id: null,
    nume: '',
    functie: '',
    email: '',
    telefon: ''
}

const emptyAddress = {
    id: null,
    nume: '',
    adresa: ''
}

function AddFurnizor({
    isOpen,
    mode = 'new',
    initialSupplier = null,
    initialContacts = null,
    initialAddresses = null,
    onClose,
    onSaved
}) {
    const logoInputRef = useRef(null)
    const [saving, setSaving] = useState(false)

    const [supplierForm, setSupplierForm] = useState(emptySupplier)
    const [contactsForm, setContactsForm] = useState([emptyContact])
    const [addressesForm, setAddressesForm] = useState([emptyAddress])

    useEffect(() => {
        if (!isOpen) return

        setSupplierForm({
            ...emptySupplier,
            ...(initialSupplier || {})
        })

        setContactsForm(
            Array.isArray(initialContacts) && initialContacts.length > 0
                ? initialContacts
                : [emptyContact]
        )

        setAddressesForm(
            Array.isArray(initialAddresses) && initialAddresses.length > 0
                ? initialAddresses
                : [emptyAddress]
        )
    }, [isOpen, initialSupplier, initialContacts, initialAddresses])

    function updateSupplierForm(field, value) {
        setSupplierForm((prev) => ({
            ...prev,
            [field]: value
        }))
    }

    function updateContactForm(index, field, value) {
        setContactsForm((prev) =>
            prev.map((contact, contactIndex) =>
                contactIndex === index
                    ? { ...contact, [field]: value }
                    : contact
            )
        )
    }

    function addContactForm() {
        setContactsForm((prev) => [...prev, { ...emptyContact }])
    }

    function removeContactForm(index) {
        setContactsForm((prev) => prev.filter((_, contactIndex) => contactIndex !== index))
    }

    function updateAddressForm(index, field, value) {
        setAddressesForm((prev) =>
            prev.map((address, addressIndex) =>
                addressIndex === index
                    ? { ...address, [field]: value }
                    : address
            )
        )
    }

    function addAddressForm() {
        setAddressesForm((prev) => [...prev, { ...emptyAddress }])
    }

    function removeAddressForm(index) {
        setAddressesForm((prev) => prev.filter((_, addressIndex) => addressIndex !== index))
    }

    async function saveSupplier() {
        try {
            setSaving(true)

            const payload = {
                id: supplierForm.id,
                logo: supplierForm.logo,
                alias: supplierForm.alias,
                imageUpload: supplierForm.imageUpload,
                nume: supplierForm.nume,
                cui: supplierForm.cui,
                website: supplierForm.website,
                website_username: supplierForm.website_username,
                website_password: supplierForm.website_password,
                note: supplierForm.note,
                contacte: contactsForm
                    .filter((contact) =>
                        contact.nume.trim() ||
                        contact.functie.trim() ||
                        contact.email.trim() ||
                        contact.telefon.trim()
                    )
                    .map((contact) => ({
                        id: contact.id || null,
                        nume: contact.nume || '',
                        functie: contact.functie || '',
                        email: contact.email || '',
                        telefon: contact.telefon || ''
                    })),
                adrese: addressesForm
                    .filter((address) =>
                        address.nume.trim() ||
                        address.adresa.trim()
                    )
                    .map((address) => ({
                        id: address.id || null,
                        nume: address.nume || '',
                        adresa: address.adresa || ''
                    }))
            }

            const response = await fetch(mode === 'new' ? NEW_SUPPLIER_API_URL : UPDATE_SUPPLIER_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })

            const data = await response.json()

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Save supplier failed')
            }

            if (onSaved) {
                await onSaved(data.supplier || data.furnizor || payload)
            }
        } catch (err) {
            console.error('SAVE SUPPLIER ERROR:', err)
            alert(err.message || 'Nu am putut salva furnizorul.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <MediumModal
            isOpen={isOpen}
            title={mode === 'new' ? 'Adaugă furnizor' : 'Editează furnizor'}
            onClose={onClose}
            footer={
                <>
                    <button
                        type="button"
                        className="modalSecondaryButton"
                        onClick={onClose}
                    >
                        Anulează
                    </button>

                    <button
                        type="button"
                        className="modalPrimaryButton"
                        disabled={saving}
                        onClick={saveSupplier}
                    >
                        {saving ? 'Se salvează...' : 'Salvează furnizor'}
                    </button>
                </>
            }
        >
            <div className="supplierPremiumModal">
                <div className="supplierPremiumCard supplierPremiumCompanyCard">
                    <aside className="supplierPremiumLogoPanel">
                        <span className="supplierPremiumLabel">Logo</span>

                        <button
                            type="button"
                            className="supplierPremiumLogoDrop"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            {supplierForm.logo ? (
                                <img src={supplierForm.logo} alt="Preview logo" />
                            ) : (
                                <svg viewBox="0 0 24 24">
                                    <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2ZM8.5 11.5l2.5 3.01L14.5 10l4.5 6H5l3.5-4.5Z" />
                                </svg>
                            )}
                        </button>

                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                const file = event.target.files?.[0]

                                if (!file) return

                                if (!file.type.startsWith('image/')) {
                                    alert('Te rog încarcă o imagine.')
                                    event.target.value = ''
                                    return
                                }

                                const reader = new FileReader()

                                reader.onload = () => {
                                    setSupplierForm((prev) => ({
                                        ...prev,
                                        logo: reader.result,
                                        imageUpload: {
                                            fileName: file.name,
                                            contentType: file.type,
                                            fileContent: reader.result
                                        }
                                    }))
                                }

                                reader.readAsDataURL(file)
                            }}
                        />

                        <button
                            type="button"
                            className="supplierPremiumGhostButton"
                            onClick={() => logoInputRef.current?.click()}
                        >
                            Upload Logo
                        </button>

                        <div className="supplierPremiumIconInput">
                            <svg viewBox="0 0 24 24">
                                <path d="M3.9 12a5 5 0 0 1 5-5h4v2h-4a3 3 0 0 0 0 6h4v2h-4a5 5 0 0 1-5-5Zm6.1 1v-2h4v2h-4Zm1-6h4a5 5 0 0 1 0 10h-4v-2h4a3 3 0 0 0 0-6h-4V7Z" />
                            </svg>

                            <input
                                type="text"
                                placeholder="https://website.com/logo.png"
                                value={supplierForm.logo}
                                onChange={(event) => {
                                    setSupplierForm((prev) => ({
                                        ...prev,
                                        logo: event.target.value,
                                        imageUpload: null
                                    }))
                                }}
                            />
                        </div>
                    </aside>

                    <section className="supplierPremiumMainPanel">
                        <div className="supplierPremiumSectionTitle">
                            <h3>Informații furnizor</h3>
                        </div>

                        <div className="supplierPremiumInfoGrid">
                            <label className="supplierPremiumField">
                                <span>Alias</span>
                                <input
                                    type="text"
                                    value={supplierForm.alias}
                                    onChange={(event) => updateSupplierForm('alias', event.target.value)}
                                />
                            </label>

                            <label className="supplierPremiumField supplierPremiumWideField">
                                <span>Nume furnizor *</span>
                                <input
                                    type="text"
                                    value={supplierForm.nume}
                                    onChange={(event) => updateSupplierForm('nume', event.target.value)}
                                />
                            </label>

                            <label className="supplierPremiumField">
                                <span>CUI</span>
                                <input
                                    type="text"
                                    value={supplierForm.cui}
                                    onChange={(event) => updateSupplierForm('cui', event.target.value)}
                                />
                            </label>

                            <label className="supplierPremiumField supplierPremiumWideField">
                                <span>Website</span>
                                <div className="supplierPremiumIconInput">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.93 9h-3.02a15.7 15.7 0 0 0-1.18-5.03A8.02 8.02 0 0 1 18.93 11ZM12 4.04c.83 1.2 1.48 3.01 1.83 4.96h-3.66C10.52 7.05 11.17 5.24 12 4.04ZM4.26 13h3.83c.14 1.77.55 3.46 1.18 5.03A8.02 8.02 0 0 1 4.26 13Zm3.83-2H4.26a8.02 8.02 0 0 1 5.01-5.03A15.7 15.7 0 0 0 8.09 11ZM12 19.96c-.83-1.2-1.48-3.01-1.83-4.96h3.66c-.35 1.95-1 3.76-1.83 4.96Zm2.73-1.93A15.7 15.7 0 0 0 15.91 13h3.02a8.02 8.02 0 0 1-4.2 5.03Z" />
                                    </svg>

                                    <input
                                        type="text"
                                        value={supplierForm.website}
                                        onChange={(event) => updateSupplierForm('website', event.target.value)}
                                    />
                                </div>
                            </label>
                        </div>

                        <div className="supplierPremiumBottomGrid">
                            <div className="supplierPremiumCredentials">
                                <label className="supplierPremiumField">
                                    <span>User</span>

                                    <div className="supplierPremiumCopyInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={supplierForm.website_username}
                                            onChange={(event) => updateSupplierForm('website_username', event.target.value)}
                                        />
                                    </div>
                                </label>

                                <label className="supplierPremiumField">
                                    <span>Password</span>

                                    <div className="supplierPremiumCopyInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9h14v-9a2 2 0 0 0-2-2Zm-7-2a2 2 0 0 1 4 0v2h-4V7Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={supplierForm.website_password}
                                            onChange={(event) => updateSupplierForm('website_password', event.target.value)}
                                        />
                                    </div>
                                </label>
                            </div>

                            <label className="supplierPremiumField supplierPremiumNotes">
                                <span>Note</span>
                                <textarea
                                    placeholder="Adaugă o notă despre furnizor..."
                                    value={supplierForm.note}
                                    onChange={(event) => updateSupplierForm('note', event.target.value)}
                                />
                            </label>
                        </div>
                    </section>
                </div>

                <div className="supplierPremiumCard supplierPremiumStripeCard supplierPremiumContactsCard">
                    <div className="supplierPremiumStripeHeader">
                        <div className="supplierPremiumSectionIcon supplierPremiumPurpleIcon">
                            <svg viewBox="0 0 24 24">
                                <path d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4ZM8 12a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8 2c-3.31 0-6 1.79-6 4v1h12v-1c0-2.21-2.69-4-6-4ZM8 14c-2.76 0-5 1.49-5 3.33V19h5v-1c0-1.26.7-2.43 1.86-3.34A6.7 6.7 0 0 0 8 14Z" />
                            </svg>
                        </div>

                        <h3>Contacte</h3>

                        <button
                            type="button"
                            className="supplierPremiumAddButton supplierPremiumPurpleButton"
                            onClick={addContactForm}
                        >
                            + Adaugă contact
                        </button>
                    </div>

                    <div className="supplierPremiumContactRows">
                        <div className="supplierPremiumContactHeader">
                            <span>Nume</span>
                            <span>Funcție</span>
                            <span>Email</span>
                            <span>Telefon</span>
                            <span></span>
                        </div>

                        {contactsForm.map((contact, index) => (
                            <div className="supplierPremiumContactRow" key={`contact-${index}`}>
                                <label className="supplierPremiumField">
                                    <span>Nume</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.nume}
                                            onChange={(event) => updateContactForm(index, 'nume', event.target.value)}
                                            placeholder="Nume complet"
                                        />
                                    </div>
                                </label>

                                <label className="supplierPremiumField">
                                    <span>Funcție</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20 6h-4V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v12h20V8a2 2 0 0 0-2-2Zm-6 0h-4V4h4v2Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.functie}
                                            onChange={(event) => updateContactForm(index, 'functie', event.target.value)}
                                            placeholder="Funcție"
                                        />
                                    </div>
                                </label>

                                <label className="supplierPremiumField">
                                    <span>Email</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5v2Z" />
                                        </svg>

                                        <input
                                            type="email"
                                            value={contact.email}
                                            onChange={(event) => updateContactForm(index, 'email', event.target.value)}
                                            placeholder="email@exemplu.ro"
                                        />
                                    </div>
                                </label>

                                <label className="supplierPremiumField">
                                    <span>Telefon</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.58.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.58 1 1 0 0 1-.24 1.01l-2.21 2.2Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={contact.telefon}
                                            onChange={(event) => updateContactForm(index, 'telefon', event.target.value)}
                                            placeholder="07xx xxx xxx"
                                        />
                                    </div>
                                </label>

                                <button
                                    type="button"
                                    className="supplierPremiumRemoveButton"
                                    disabled={contactsForm.length === 1}
                                    onClick={() => removeContactForm(index)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM8 4l1-1h6l1 1h4v2H4V4h4Z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="supplierPremiumCard supplierPremiumStripeCard supplierPremiumAddressesCard">
                    <div className="supplierPremiumStripeHeader">
                        <div className="supplierPremiumSectionIcon supplierPremiumGreenIcon">
                            <svg viewBox="0 0 24 24">
                                <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
                            </svg>
                        </div>

                        <h3>Adrese</h3>

                        <button
                            type="button"
                            className="supplierPremiumAddButton supplierPremiumGreenButton"
                            onClick={addAddressForm}
                        >
                            + Adaugă adresă
                        </button>
                    </div>

                    <div className="supplierPremiumAddressRows">
                        <div className="supplierPremiumAddressHeader">
                            <span>Nume adresă</span>
                            <span>Adresă completă</span>
                            <span></span>
                        </div>

                        {addressesForm.map((address, index) => (
                            <div className="supplierPremiumAddressRow" key={`address-${index}`}>
                                <label className="supplierPremiumField">
                                    <span>Nume adresă</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={address.nume}
                                            onChange={(event) => updateAddressForm(index, 'nume', event.target.value)}
                                            placeholder="Ex: Sediu principal"
                                        />
                                    </div>
                                </label>

                                <label className="supplierPremiumField supplierPremiumAddressField">
                                    <span>Adresă completă</span>
                                    <div className="supplierPremiumIconInput">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M20.5 3 15 5.2 9 3 3.5 5.2V21L9 18.8l6 2.2 5.5-2.2V3ZM10 5.4l4 1.47v11.73l-4-1.47V5.4Z" />
                                        </svg>

                                        <input
                                            type="text"
                                            value={address.adresa}
                                            onChange={(event) => updateAddressForm(index, 'adresa', event.target.value)}
                                            placeholder="Stradă, număr, oraș, județ, cod poștal"
                                        />
                                    </div>
                                </label>

                                <button
                                    type="button"
                                    className="supplierPremiumRemoveButton"
                                    disabled={addressesForm.length === 1}
                                    onClick={() => removeAddressForm(index)}
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM8 4l1-1h6l1 1h4v2H4V4h4Z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </MediumModal>
    )
}

export default AddFurnizor